import {Button} from '@wandb/weave/components';
import {maybePluralizeWord} from '@wandb/weave/core/util/string';
import React, {useCallback, useMemo, useState} from 'react';

import {useDispatch} from '../../../state/hooks';
import * as PollingActions from '../../../state/polling/actions';
import {Run} from '../../../util/runTypes';
import {InstrumentedCenteredWaveLoader} from '../../utility/InstrumentedLoader';
import {InlineCombobox} from './InlineCombobox/InlineCombobox';
import {Body, Container, Footer, Header, Progress} from './MoveRunDrawerPieces';
import {useTrackRunsWithLocation} from './moveRunsEvents';
import {useRunGroups} from './useRunGroups';
import {useRunMover} from './useRunMover';

type MoveRunDrawerContents = {
  entityName: string;
  isInReport: boolean | undefined;
  onClose: () => void;
  onMovedRuns: () => void;
  projectName: string;
  runs: Run[];
};

export function MoveRunDrawerContents({
  entityName,
  isInReport,
  onClose,
  onMovedRuns,
  projectName,
  runs,
}: MoveRunDrawerContents) {
  const dispatch = useDispatch();
  const {
    groups,
    loading,
    refetch: refetchRunGroups,
  } = useRunGroups(entityName, projectName);
  const [selectedGroup, setSelectedGroup] = useState<string | null>(null);
  const [manuallyAddedGroups, setManuallyAddedGroups] = useState<string[]>([]);
  const [shouldRefetchGroups, setShouldRefetchGroups] = useState(false);
  const [moveRuns, moveRunState] = useRunMover({
    groupName: selectedGroup,
    entityName,
    projectName,
  });
  const trackMoveRunsWithLocation = useTrackRunsWithLocation();

  const allRunsInSameGroupName = useMemo(() => {
    if (runs.length === 0) {
      return undefined;
    }
    const firstGroupName = runs[0].group;
    const allSameGroup = runs.every(r => r.group === firstGroupName);
    return allSameGroup ? firstGroupName : undefined;
  }, [runs]);

  const onMoveRuns = async () => {
    await moveRuns(runs);

    // send poll tick to trigger runs data refetches (e.g. RSR, RSDQ, bRSDQ)
    // so the change is reflected in the relevant UIs (run selector, charts)
    dispatch(PollingActions.tick());

    if (shouldRefetchGroups) {
      await refetchRunGroups();
      setShouldRefetchGroups(false);
    }
    onMovedRuns();
  };

  const onCancel = () => {
    if (moveRunState.status === 'running') {
      // Clicking cancel while the move is in progress cancels outstanding runs
      // if the user hits ok on the confirm dialog.
      const response = confirm(
        `Clicking 'Ok' will stop moving remaining runs to ${selectedGroup}.`
      );
      if (response) {
        moveRunState.cancel();
      }
    } else {
      onClose();
    }
  };

  const title = `Move ${maybePluralizeWord(
    runs.length,
    'run'
  )} to another group`;

  const label =
    runs.length === 1
      ? `Move ${runs[0].displayName} to:`
      : `Move ${runs.length} runs to:`;

  const isMovingRuns = moveRunState.status === 'running';
  const onAddItem = useCallback(
    item => {
      setSelectedGroup(item);
      setManuallyAddedGroups(prev => {
        if (prev.includes(item)) {
          return prev;
        }
        return [item, ...prev];
      });
      setShouldRefetchGroups(true);
    },
    [setSelectedGroup, setManuallyAddedGroups, setShouldRefetchGroups]
  );

  const items = useMemo(() => {
    return [...manuallyAddedGroups, ...groups].map(group => ({
      name: group,
      isDisabled: group === allRunsInSameGroupName,
      disabledMessage:
        group === allRunsInSameGroupName
          ? 'Selected runs are already in this group'
          : undefined,
    }));
  }, [manuallyAddedGroups, groups, allRunsInSameGroupName]);

  return (
    <Container isInReport={isInReport}>
      <Header onClose={isMovingRuns ? null : onClose} title={title} />
      <Body>
        {loading ? (
          <InstrumentedCenteredWaveLoader name="load-all-groups" size="small" />
        ) : (
          <InlineCombobox
            disabled={isMovingRuns}
            emptyLabel="No groups match this search"
            emptyDescription="Try another search term or explore available groups."
            // If the user has created a new group, the selected group will not appear in
            // the groups provided by the server, _unless_ they accidentally typed in an
            // existing group name. If they have typed in a new unique name, stick it at
            // the front of the list.
            items={items}
            itemToString={v => v?.name || ''}
            label={label}
            newItemButtonLabel="New group"
            newItemPlaceholder="Type a group name..."
            onAddItem={item => {
              onAddItem(item);
              trackMoveRunsWithLocation('Created group');
            }}
            onSelectedItemChange={item =>
              setSelectedGroup(item?.name ? item.name : null)
            }
            isItemDisabled={item => item?.isDisabled ?? false}
            disabledItemMessage={item => {
              return item?.disabledMessage;
            }}
            placeholder="Search groups"
            selectedItem={
              selectedGroup
                ? items.find(item => item.name === selectedGroup)
                : null
            }
          />
        )}
      </Body>
      {isMovingRuns && moveRunState.total > 10 ? (
        <Footer>
          <p className="grow">
            Moving run {moveRunState.progress} of {moveRunState.total}
          </p>
          <p>
            {Math.round((moveRunState.progress / moveRunState.total) * 100)}%
          </p>
          <Progress value={moveRunState.progress} max={moveRunState.total} />
        </Footer>
      ) : null}
      <Footer>
        <Button
          variant="secondary"
          data-test="cancel-move-runs"
          className="w-full"
          onClick={onCancel}>
          Cancel
        </Button>
        <Button
          icon={isMovingRuns ? 'loading' : undefined}
          aria-label={'Move runs'}
          data-test="move-runs"
          disabled={isMovingRuns || !selectedGroup}
          className="w-full"
          onClick={() => onMoveRuns()}>
          {isMovingRuns ? 'Moving...' : 'Move'}
        </Button>
      </Footer>
    </Container>
  );
}
